import {
  Apikey,
  ApikeyWithKey,
  SetStorageSettingsImplementationEnum,
  User,
} from "../lib/cnb/api/public";
import { apikeyApi, stripeApi, userApi, magicTokenApi } from "../cnb/apis";
import { getMagicTokenEnv } from "../lib/cnb/env";

const api = {
  getCurrentUser: async (): Promise<User | null> => {
    return userApi.getUser();
  },

  register: async (
    email: string,
    password: string | undefined,
    emailOptIn: boolean,
    signupSource: string,
  ) => {
    await userApi.createUser({
      userCreateRequest: { email, password, emailOptIn, signupSource },
    });
  },

  login: async (
    email: string,
    password: string,
    remember: boolean,
  ): Promise<User | null> => {
    await userApi.login({ email, password, remember });
    return userApi.getUser();
  },

  logout: async () => {
    await userApi.logout();
    return null;
  },

  requestMagicLink: async (email: string) => {
    return await magicTokenApi.requestNewToken({
      magicTokenAnonymousRequest: {
        email,
        target: getMagicTokenEnv,
      },
    });
  },

  updateUser: async (user: Partial<User>): Promise<User> => {
    return userApi.updateUser({ userUpdateRequest: { name: user.name! } });
  },

  enableEmailOptInPreference: async () => {
    return userApi.enableEmailOptInPreference();
  },

  removeEmailOptInPreference: async () => {
    return userApi.removeEmailOptInPreference();
  },

  updatePassword: async (currentPassword: string, newPassword: string) => {
    await userApi.updatePassword({ newPassword, currentPassword });
  },

  requestPasswordReset: async (emailAddress: string) => {
    await userApi.requestPasswordReset({ emailAddress });
  },

  resetPassword: async (token: string, newPassword: string) => {
    await userApi.resetPassword({ token, newPassword });
  },

  getApikeys: async (): Promise<Apikey[]> => apikeyApi.getAll(),

  createApikey: async (name: string): Promise<ApikeyWithKey> =>
    apikeyApi.create({ apikeyCreateRequest: { name } }),

  deleteApikey: async (id: string) => {
    await apikeyApi.delete({ apikeyId: id });
  },

  openPaymentHistory: async () => {
    const { url } = await stripeApi.createBillingPortal();
    window.open(url);
  },

  requestPaymentHistoryByEmail: () => stripeApi.requestBillingPortal(),

  setStorageImplementation: async (
    storageImplementation: SetStorageSettingsImplementationEnum,
  ) => {
    await userApi.setStorageSettings({ implementation: storageImplementation });
  },
};

export default api;

const demoLogin = (email: string, password: string) => {
  const user: User = {
    name: "Demo User",
    marketingData: {
      emailOptIn: false,
      signupSource: "foobar",
    },
    ...JSON.parse(localStorage.getItem("usertoken") || "{}"),
    id: String(Math.floor(Math.random() * 1000)),
    email,
    password,
  };
  localStorage.setItem("usertoken", JSON.stringify(user));
  return Promise.resolve(user);
};

const demoGetApikeys = <K>(): K[] =>
  JSON.parse(localStorage.getItem("apikeys") || "[]");

const demoSetApikeys = (apikeys: unknown[]) => {
  localStorage.setItem("apikeys", JSON.stringify(apikeys));
};

export const demoApi: typeof api = {
  getCurrentUser: (): Promise<User | null> => {
    const user = JSON.parse(localStorage.getItem("usertoken") || "null");
    return Promise.resolve(user);
  },

  register: () => Promise.resolve(),

  login: (email: string, password: string, remember: boolean) => {
    if (!remember) {
      setTimeout(() => localStorage.removeItem("usertoken"), 10 * 1000);
    }
    return demoLogin(email, password);
  },

  logout: () => {
    localStorage.removeItem("usertoken");
    return Promise.resolve(null);
  },

  requestMagicLink: (email: string) => {
    return Promise.resolve({
      userEmail: email,
      status: "ok",
    });
  },

  updateUser: (user: Partial<User>) => {
    const updatedUser = {
      ...JSON.parse(localStorage.getItem("usertoken") || "{}"),
      ...user,
    };
    localStorage.setItem("usertoken", JSON.stringify(updatedUser));
    return Promise.resolve(updatedUser);
  },

  updatePassword: (currentPassword: string, newPassword: string) => {
    const user = JSON.parse(localStorage.getItem("usertoken") || "{}");
    if (user.password !== currentPassword) {
      return Promise.reject(new Error("Current password incorrect"));
    }
    localStorage.setItem(
      "usertoken",
      JSON.stringify({
        ...user,
        password: newPassword,
      }),
    );
    return Promise.resolve();
  },

  requestPasswordReset: () => Promise.resolve(),

  resetPassword: () => Promise.resolve(),

  enableEmailOptInPreference: () =>
    Promise.resolve(JSON.parse(localStorage.getItem("usertoken") || "{}")),
  removeEmailOptInPreference: () =>
    Promise.resolve(JSON.parse(localStorage.getItem("usertoken") || "{}")),

  getApikeys: <K>() =>
    Promise.resolve(
      demoGetApikeys<K>().map((k) => ({
        ...k,
        key: null,
      })),
    ),

  createApikey: (name: string) => {
    const apikeys = demoGetApikeys<any>();
    apikeys.push({
      id: String(Date.now()),
      name,
      key: String(Date.now()),
    });
    demoSetApikeys(apikeys);
    return Promise.resolve(apikeys[apikeys.length - 1]);
  },

  deleteApikey: (id: string) => {
    const apikeys = demoGetApikeys<any>();
    demoSetApikeys(apikeys.filter((d) => d.id !== id));
    return Promise.resolve();
  },

  openPaymentHistory: () => {
    window.open("https://github.com");
    return Promise.resolve();
  },

  requestPaymentHistoryByEmail: () => Promise.resolve({ success: true }),

  setStorageImplementation: () => {
    return Promise.resolve();
  },
};
